import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getDefaultRoute } from "@/lib/permissions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { SignIn, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

const BG_IMG =
  "https://images.unsplash.com/photo-1633206327875-6d17672c804d?fm=jpg&q=80&w=2400&auto=format&fit=crop";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(username, password);
      toast.success("Sesión iniciada");
      nav(getDefaultRoute(data), { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al iniciar sesión";
      toast.error(typeof msg === "string" ? msg : "Error al iniciar sesión");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5">
      <div
        className="hidden lg:block lg:col-span-3 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.25), rgba(0,0,0,0.35)), url(${BG_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute top-8 left-8 flex items-center gap-5">
          <img
            src="https://customer-assets.emergentagent.com/job_carne-contable/artifacts/2jcyugqt_logo-1625602837-1684335351-d041931afd0dbc075ba09060badaa6f21684335352.png"
            alt="La Bife SRL"
            className="h-24 w-auto object-contain"
            data-testid="login-logo"
          />
          <div>
            <div
              className="font-heading font-bold text-4xl xl:text-5xl uppercase"
              style={{ color: "#F5EAD8", letterSpacing: "0.12em" }}
            >
              LA BIFE SRL
            </div>
          </div>
        </div>
        <div className="absolute bottom-10 left-10 max-w-lg">
        </div>
      </div>

      <div className="lg:col-span-2 bg-white flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#1A1A1A]">Iniciar sesión</h1>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="username" className="text-[12px] text-[#333]">Usuario</Label>
              <Input
                id="username"
                data-testid="login-email"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 rounded-sm"
                placeholder="ej: admin"
                required
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[12px] text-[#333]">Contraseña</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm pr-10"
                  required
                />
                <button
                  type="button"
                  data-testid="toggle-login-password"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#666] hover:text-[#1A1A1A]"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-sm bg-[#A02836] hover:bg-[#821E2A] text-white h-10 font-medium"
          >
            <SignIn size={16} className="mr-2" />
            {busy ? "Ingresando…" : "Ingresar"}
          </Button>
        </form>
      </div>
    </div>
  );
}
